import type { TransferGroupMember, TransferGroupMemberEndpointType, UserWithGroups } from '../../types';

export const RING_TIMEOUT_DEFAULT_SECONDS = 20;
export const RING_TIMEOUT_MIN_SECONDS = 5;
export const RING_TIMEOUT_MAX_SECONDS = 60;

export interface TransferGroupEndpointFormRow {
  rowKey: string;
  endpointType: TransferGroupMemberEndpointType;
  value: string;
  ringTimeoutSeconds: number;
  isActive: boolean;
}

export interface TransferGroupMemberFormRow {
  rowKey: string;
  id?: string;
  kind: 'user' | 'external';
  userId: string | null;
  userLabel: string;
  displayName: string;
  selectionKey: string;
  aliases: string[];
  description: string;
  fallbackSelectionKey: string;
  endpoints: TransferGroupEndpointFormRow[];
  isActive: boolean;
}

let nextFormRowKey = 0;

/** Return a key that identifies one form row across reorders. */
export function newFormRowKey(prefix: string): string {
  nextFormRowKey += 1;
  return `${prefix}-${nextFormRowKey}`;
}

export function newEndpointRow(endpointType: TransferGroupMemberEndpointType = 'external_extension'): TransferGroupEndpointFormRow {
  return {
    rowKey: newFormRowKey('endpoint'),
    endpointType,
    value: '',
    ringTimeoutSeconds: RING_TIMEOUT_DEFAULT_SECONDS,
    isActive: true,
  };
}

export function userMemberRow(user: UserWithGroups): TransferGroupMemberFormRow {
  return {
    rowKey: newFormRowKey('member'),
    kind: 'user',
    userId: user.id,
    userLabel: user.email,
    displayName: '',
    selectionKey: '',
    aliases: [],
    description: '',
    fallbackSelectionKey: '',
    endpoints: [],
    isActive: true,
  };
}

export function externalMemberRow(): TransferGroupMemberFormRow {
  return {
    rowKey: newFormRowKey('member'),
    kind: 'external',
    userId: null,
    userLabel: '',
    displayName: '',
    selectionKey: '',
    aliases: [],
    description: '',
    fallbackSelectionKey: '',
    endpoints: [newEndpointRow()],
    isActive: true,
  };
}

/** Map an API member to its editable form row. */
export function memberFormRow(member: TransferGroupMember): TransferGroupMemberFormRow {
  return {
    rowKey: member.id,
    id: member.id,
    kind: member.kind ?? (member.userId ? 'user' : 'external'),
    userId: member.userId,
    userLabel: member.userEmail ?? '',
    displayName: member.displayName ?? '',
    selectionKey: member.selectionKey ?? '',
    aliases: member.aliases ?? [],
    description: member.description ?? '',
    fallbackSelectionKey: member.fallbackSelectionKey ?? '',
    endpoints: (member.endpoints ?? []).map((endpoint) => ({
      rowKey: endpoint.id,
      endpointType: endpoint.endpointType,
      value: endpoint.endpointType === 'external_phone' ? endpoint.externalPhoneE164 : endpoint.extension,
      ringTimeoutSeconds: endpoint.ringTimeoutSeconds,
      isActive: endpoint.isActive,
    })),
    isActive: member.isActive,
  };
}

/**
 * Build the API payload of one member. Queue groups only send the user and
 * the active flag; agent-selected groups send every selection field.
 */
export function memberPayload(member: TransferGroupMemberFormRow, agentSelected: boolean) {
  const identity = member.id ? { id: member.id } : {};
  if (!agentSelected) {
    return { ...identity, userId: member.userId, isActive: member.isActive };
  }
  return {
    ...identity,
    userId: member.kind === 'user' ? member.userId : null,
    displayName: member.displayName.trim(),
    selectionKey: member.selectionKey.trim(),
    aliases: member.aliases,
    description: member.description.trim(),
    fallbackSelectionKey: member.fallbackSelectionKey,
    endpoints: member.kind === 'external'
      ? member.endpoints.map((endpoint, index) => ({
        endpointType: endpoint.endpointType,
        extension: endpoint.endpointType === 'external_extension' ? endpoint.value.trim() : '',
        externalPhoneE164: endpoint.endpointType === 'external_phone' ? endpoint.value.trim() : '',
        priority: (index + 1) * 10,
        ringTimeoutSeconds: endpoint.ringTimeoutSeconds,
        isActive: endpoint.isActive,
      }))
      : [],
    isActive: member.isActive,
  };
}

/** Return the first readable message inside a nested serializer error payload. */
export function firstNestedMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    return value.trim() || null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstNestedMessage(item);
      if (message) return message;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    return firstNestedMessage(Object.values(value as Record<string, unknown>));
  }
  return null;
}
